import { ref } from 'vue'
import { defineStore } from 'pinia'
import { formatISO } from 'date-fns'
import { useRouter } from 'vue-router'
import { cloneDeep } from 'lodash'
import { filterApi } from '../api/filter.api'
import { useClientStore } from '@/modules/clients/stores/client.store'
import { useQueryStore } from '@/modules/services/query/stores/query.store'
import { useTheme } from '@/modules/clients/composables/use-theme'
import { useToaster } from '@/modules/toaster/composables/use-toaster'
import { toISOWithOffset } from '@/shared/utils/date'
import { ClientSetting } from '@/modules/clients/types/client.enums'
import { GotoType } from '@/modules/services/query/types/query.enums'
import { DEFAULT_FILTER_QUERY } from '../constants/filter.constants'
import type { Action, Brand, Filter, FilterPreset } from '../models/filter.model'
import type { QueryParams } from '@/modules/services/query/models/query.model'
import type { TagContext, DimensionKey } from '../models/filter.types'

export const useFilterStore = defineStore('filter', () => {
  const router = useRouter()
  const clientStore = useClientStore()
  const queryStore = useQueryStore()
  const theme = useTheme()

  const brand = ref<Brand>()
  const tagContext = ref<TagContext>()

  const { warning } = useToaster()

  const filter = ref<Filter>(cloneDeep(DEFAULT_FILTER_QUERY))

  const setDefaultFilter = (): void => {
    filter.value = cloneDeep(DEFAULT_FILTER_QUERY)
  }

  const setSearchTag = (search: string): void => {
    tagContext.value = {
      search,
    }
  }

  const setRelatedIdTag = (relatedId: number): void => {
    tagContext.value = {
      relatedId,
    }
  }

  const setShipmentTag = (shipmentNo: string): void => {
    tagContext.value = {
      shipmentNo,
    }
  }

  const clearTag = () => {
    tagContext.value = undefined
    router.replace({
      query: {},
    })
  }

  const warnNotFound = (param?: string, value?: string): void => {
    warning('message.paramNotFound', {
      param: param,
      value: value,
    })
  }

  const getDimensionFilter = (
    dimensions?: Partial<Record<DimensionKey, number | null>>,
  ): Record<DimensionKey, number | undefined> => {
    return {
      1: dimensions?.[1] ?? undefined,
      2: dimensions?.[2] ?? undefined,
      3: dimensions?.[3] ?? undefined,
    }
  }

  const getCarrierId = (carrierName?: string): number | undefined => {
    const carrier = clientStore.carriers.find((carrier) => carrier.name === carrierName)

    if (!carrier && carrierName) warnNotFound('Carrier', carrierName)

    return carrier ? carrier.id : undefined
  }

  const getCountryId = (countryName?: string): number | undefined => {
    const country = clientStore.countries.find((country) => country.name === countryName)

    if (!country && countryName) warnNotFound('Country', countryName)

    return country ? country.id : undefined
  }

  const getServiceGroupId = (serviceGroupName?: string): number | undefined => {
    const serviceGroup = clientStore.serviceGroups.find(
      (serviceGroup) => serviceGroup.name === serviceGroupName,
    )

    if (!serviceGroup && serviceGroupName) warnNotFound('Service group', serviceGroupName)

    return serviceGroup ? serviceGroup.id : undefined
  }

  const getZipZoneId = (zipZoneName?: string): number | undefined => {
    const zipZone = clientStore.zipZones.find((zipZone) => zipZone.name === zipZoneName)

    if (!zipZone && zipZoneName) warnNotFound('Zip zone', zipZoneName)

    return zipZone ? zipZone.id : undefined
  }

  const getAction = (actionId?: number): Action | undefined => {
    const action = clientStore.actions.find((action) => action.groupId === actionId)

    if (!action && actionId) warnNotFound('Action', actionId.toString())

    return action ? action : undefined
  }

  const getDimension = (index: number, dimensionName?: string): number | undefined => {
    const dimension = clientStore.dimensions[index]?.values.find(
      (dimension) => dimension.value === dimensionName,
    )

    if (!dimension && dimensionName) warnNotFound(`Dimension ${index}`, dimensionName)

    return dimension ? dimension.id : undefined
  }

  const setFilterPreset = (preset: FilterPreset): void => {
    filter.value.fromViewDate = toISOWithOffset(preset.fromViewDate)
    filter.value.toViewDate = toISOWithOffset(preset.toViewDate)
    filter.value.fromActionDate = toISOWithOffset(preset.fromActionDate)
    filter.value.toActionDate = toISOWithOffset(preset.toActionDate)
    filter.value.carrierId = preset.carrierId
    filter.value.countryId = preset.countryId
    filter.value.serviceGroupId = preset.serviceGroupId
    filter.value.zipZoneId = preset.zipZoneId
    filter.value.dimensions = getDimensionFilter(preset.dimensions)
    filter.value.isAssignedToMe = clientStore.isSettingEnabled(ClientSetting.MyShipmentValue)
    filter.value.status = preset.status || []
  }

  const isStatusWithinRange = (statusId: number): boolean => statusId > 0 && statusId < 8

  const setStatus = (value?: string | string[]): number[] => {
    const defaultStatus = [1, 2, 3, 4, 5, 6, 7]
    const input = value

    if (!input) {
      return defaultStatus
    }

    const values = Array.isArray(input) ? input : [input]

    const status = values.reduce<number[]>((acc, s) => {
      const statusId = parseInt(s.toString())

      if (isStatusWithinRange(statusId)) {
        acc.push(statusId)
      } else {
        warning('message.paramNotFound', { key: 'Status', param: statusId })
      }

      return acc
    }, [])

    if (!Array.isArray(input) && status.length === 0) {
      return defaultStatus
    }

    return status
  }

  const setQueryParam = (queryParam: QueryParams): void => {
    if (queryParam.type !== GotoType.Filter) return

    filter.value.isViewDate = false
    filter.value.isActionDate = false

    filter.value.action = getAction(queryParam.actionId)

    const { actionId, fromActionDate, toActionDate, fromViewDate, toViewDate } = queryParam

    if (actionId && fromActionDate && toActionDate) {
      filter.value.fromActionDate = formatISO(fromActionDate)
      filter.value.toActionDate = formatISO(toActionDate)
      filter.value.isActionDate = true
    }

    if (fromViewDate && toViewDate) {
      filter.value.fromViewDate = formatISO(fromViewDate)
      filter.value.toViewDate = formatISO(toViewDate)
      filter.value.isViewDate = true
    }

    if (!fromActionDate && !toActionDate && !fromViewDate && !toViewDate) {
      const now = formatISO(new Date())
      filter.value.fromViewDate = now
      filter.value.toViewDate = now
      filter.value.isViewDate = true
    }

    filter.value.carrierId = getCarrierId(queryParam.carrierName)
    filter.value.countryId = getCountryId(queryParam.countryName)
    filter.value.serviceGroupId = getServiceGroupId(queryParam.serviceGroupName)
    filter.value.zipZoneId = getZipZoneId(queryParam.zipZoneName)

    filter.value.isAssignedToMe = queryParam.isAssignedToMe
    filter.value.status = setStatus(queryParam.status)

    const dimensionInputs = [
      queryParam.dimension1Value,
      queryParam.dimension2Value,
      queryParam.dimension3Value,
    ]

    filter.value.dimensions = Object.fromEntries(
      dimensionInputs.map((value, i) => [i + 1, getDimension(i + 1, value)]),
    )
  }

  const setTheme = (color?: Brand): void => {
    brand.value = color
    theme.apply(color)
  }

  const loadFilter = async (): Promise<void> => {
    setDefaultFilter()

    const response = await filterApi.getFilterPreset()

    if (response.error || !response.data) return

    setTheme(response.data.brand)

    const queryParams = queryStore.queryParams

    switch (queryParams.type) {
      case GotoType.Shipment:
        setShipmentTag(queryParams.shipmentNo)
        return
      case GotoType.Filter:
        setQueryParam(queryParams)
        return
      default:
        setFilterPreset(response.data.preset)
        return
    }
  }

  return {
    filter,
    brand,
    tagContext,
    setSearchTag,
    setRelatedIdTag,
    setShipmentTag,
    clearTag,
    loadFilter,
  }
})
